import { SPACE_TYPE_LABELS, STORAGE_TYPE_LABELS, type StorageRequestSummary } from '@/shared/storage-api';
import { formatCalendarDateShort } from '@/shared/format';

/**
 * NEX-1165 design pass (spec 2026-10-18 §3.1/§3.2) — how a storage request reads in the Solicitudes
 * rows and on the Cotizar page. Formatting only: every line comes from fields the inbox already
 * sends. Shared by `SolicitudesPage.tsx` and `QuoteForm.tsx`, kept out of either page so neither
 * imports the other's internals.
 */

/**
 * Audit S2 — who takes care of the transfer to the depósito, as the client chose in the wizard
 * (NEX-1166: "Sí, cotizarlo con Nexvia" / "Sí, lo resuelvo yo" / no transfer). `undefined`/`null`
 * (an API that does not send `transportQuotedByNexvia` yet) only says that it needs one.
 * Moved unchanged from `SolicitudesPage.tsx` so the Cotizar page shows the same four texts.
 */
export function transportLabel(request: Pick<StorageRequestSummary, 'needsTransport' | 'transportQuotedByNexvia'>): string {
  if (!request.needsTransport) return 'Traslado: no lo necesita';
  if (request.transportQuotedByNexvia === true) return 'Traslado: lo cotiza Nexvia';
  if (request.transportQuotedByNexvia === false) return 'Traslado: lo resuelve el cliente';
  return 'Necesita traslado';
}

/** The type's own label when the catalog cannot name it: the seeded codes, or the code as sent. */
export function fallbackStorageTypeLabel(code: string): string {
  return STORAGE_TYPE_LABELS[code] ?? code;
}

const VOLUME_FORMAT = new Intl.NumberFormat('es-UY', { maximumFractionDigits: 2 });

/** The declared volume, "≈ 8 m³" for a predefined box (an estimate) and "8 m³" for a custom one. */
function volumeText(request: Pick<StorageRequestSummary, 'spaceType' | 'volumeM3'>): string | null {
  if (!request.volumeM3) return null;
  return `${request.spaceType === 'personalizado' ? '' : '≈ '}${VOLUME_FORMAT.format(request.volumeM3)} m³`;
}

/**
 * "≈ 8 m³ · privado": the volume, then the storage type in lower case. Without a volume, the space
 * label stands in for it ("Box mediano · privado").
 */
export function volumeLine(request: Pick<StorageRequestSummary, 'spaceType' | 'volumeM3'>, storageTypeLabel: string): string {
  const space = volumeText(request) ?? SPACE_TYPE_LABELS[request.spaceType] ?? request.spaceType;
  return `${space} · ${storageTypeLabel.toLowerCase()}`;
}

/** The "Espacio" value of Cotizar's summary: "≈ 8 m³", or that no volume came with the request. */
export function volumeValue(request: Pick<StorageRequestSummary, 'spaceType' | 'volumeM3'>): string {
  return volumeText(request) ?? 'Sin volumen declarado';
}

/**
 * The size chip of a Solicitudes row: the box the client picked, or "Personalizado · volumen
 * declarado" (the request does not keep whether the volume was typed or computed from objects, so
 * the mock's "calculado por objetos" variant cannot be told apart — spec §3.1).
 */
export function spaceChipLabel(request: Pick<StorageRequestSummary, 'spaceType'>): string {
  if (request.spaceType === 'personalizado') return 'Personalizado · volumen declarado';
  return SPACE_TYPE_LABELS[request.spaceType] ?? request.spaceType;
}

function monthsText(months: number): string {
  return `${months} ${months === 1 ? 'mes' : 'meses'}`;
}

/**
 * The request's LAST DAY INCLUDED, the same rule as the API's `resolveStoragePeriod`
 * (nexvia-api-web `storage-period.ts`, NEX-1170 C5): `periodStart` plus `periodMonths` calendar
 * months — a day the target month lacks clamps to its last day (31 ene. + 1 → 28 feb.) — minus one
 * day. So 1 set. 2026 + 6 → 28 feb. 2027 and 31 ene. 2026 + 1 → 27 feb. 2026. Read in UTC like
 * `formatCalendarDate` (a SQL `date` travels as midnight UTC). `null` for an open-ended request or
 * one without a duration. (The API also moves a start already in the past to the payment day; the
 * panel shows the request as published, before any payment, so it keeps `periodStart`.)
 */
export function periodEnd(request: Pick<StorageRequestSummary, 'periodStart' | 'periodMonths' | 'openEnded'>): string | null {
  if (request.openEnded || !request.periodMonths) return null;
  const start = new Date(request.periodStart);
  if (Number.isNaN(start.getTime())) return null;
  const year = start.getUTCFullYear();
  const month = start.getUTCMonth() + request.periodMonths;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(start.getUTCDate(), lastDay) - 1)).toISOString();
}

type PeriodFields = Pick<StorageRequestSummary, 'periodStart' | 'periodMonths' | 'openEnded'>;

/**
 * Audit S1 — only a `verified` account can quote (the API answers 403 `STORAGE_NOT_APPROVED`
 * otherwise); `pending_review` can already read the inbox (NEX-1164, "leer sí, cotizar no"). Shared by
 * the list (why "Cotizar" is off) and the Cotizar page (why there is no form), so both say the same.
 */
export const QUOTE_DISABLED_REASON =
  'Vas a poder cotizar cuando Nexvia apruebe tu alta. Mientras tanto podés ver las solicitudes de tu zona; revisá el estado de tu cuenta en Mi empresa.';

/** "1 set. 2026" — the request's first day. */
export function periodStartText(request: Pick<StorageRequestSummary, 'periodStart'>): string {
  return formatCalendarDateShort(request.periodStart) ?? request.periodStart;
}

/** The period chip of a Solicitudes row: "6 meses desde 1 set. 2026" / "Sin fecha de fin desde 1 set. 2026". */
export function periodChipLabel(request: PeriodFields): string {
  const start = periodStartText(request);
  if (request.openEnded) return `Sin fecha de fin desde ${start}`;
  if (request.periodMonths) return `${monthsText(request.periodMonths)} desde ${start}`;
  return `Desde ${start}`;
}

/**
 * Cotizar's period chips: "6 meses desde el 1 set. 2026" and "hasta el 28 feb. 2027" (`until` is
 * `null` for an open-ended request or one without a duration). Two chips, not one line, so the header
 * wraps at 375px instead of overflowing (review fix).
 */
export function periodChipTexts(request: PeriodFields): { from: string; until: string | null } {
  const start = periodStartText(request);
  if (request.openEnded) return { from: `Sin fecha de fin desde el ${start}`, until: null };
  const end = formatCalendarDateShort(periodEnd(request));
  if (request.periodMonths && end) return { from: `${monthsText(request.periodMonths)} desde el ${start}`, until: `hasta el ${end}` };
  return { from: `Desde el ${start}`, until: null };
}

/** Cotizar's "Duración": "6 meses · hasta el 28 feb. 2027" or "Sin fecha de fin". */
export function durationText(request: PeriodFields): string {
  if (request.openEnded) return 'Sin fecha de fin';
  if (!request.periodMonths) return 'Sin indicar';
  const end = formatCalendarDateShort(periodEnd(request));
  return end ? `${monthsText(request.periodMonths)} · hasta el ${end}` : monthsText(request.periodMonths);
}

/**
 * The full-period price's unit (spec §3.2: "6 meses", or "sin fecha de fin" for an open request).
 * Shown as the field's hint: `Input` has no prefix/suffix slot.
 */
export function fullPeriodUnit(request: Pick<StorageRequestSummary, 'periodMonths' | 'openEnded'>): string {
  if (request.openEnded) return 'sin fecha de fin';
  return request.periodMonths ? monthsText(request.periodMonths) : 'el período completo';
}
